/**
 * Public block renderer — takes a CMS page (with sections & blocks) and renders it.
 * Supports every block type declared in blockTypes.js.
 * Used by HomePage and any dynamic CMS page.
 */
import { Link } from "react-router-dom";
import { useState } from "react";
import * as Icons from "lucide-react";
import workbenchImage from "@/assets/images/sqithome.png";

function Icon({ name, ...rest }) {
  const key = (name || "").split("-").map((w) => w[0]?.toUpperCase() + w.slice(1)).join("");
  const C = Icons[key] || Icons.Sparkles;
  return <C {...rest} />;
}

function Section({ children, className = "", id }) {
  return (
    <section id={id} className={`sqit-section ${className}`}>
      <div className="sqit-container">{children}</div>
    </section>
  );
}

function SectionHead({ overline, heading }) {
  if (!overline && !heading) return null;
  return (
    <div className="max-w-2xl">
      {overline && <div className="text-xs font-semibold uppercase tracking-widest text-[#538135]">{overline}</div>}
      {heading && <h2 className="mt-2 font-display text-3xl sm:text-4xl font-semibold text-gray-900">{heading}</h2>}
    </div>
  );
}

// ---------- Individual blocks ----------

function HeroBlock({ data }) {
  const isSoftwareHero = data?.image === "sqit-workbench";
  return (
    <div className={`relative overflow-hidden ${isSoftwareHero ? "bg-gradient-to-br from-white via-[#F8FAF7] to-[#EAF4E3]/40" : ""}`}>
      <div className="absolute inset-0 hero-grid pointer-events-none" />
      <div className="sqit-container pt-24 pb-20 sm:pt-32 sm:pb-28 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            {data?.overline && (
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EAF4E3] text-[#09582D] text-[11px] font-semibold tracking-[0.18em]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#538135]" />
                {data.overline}
              </div>
            )}
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 leading-[1.05]">
              {data?.headline}
            </h1>
            {data?.subheadline && (
              <p className="mt-6 text-lg text-gray-600 leading-relaxed max-w-xl">{data.subheadline}</p>
            )}
            <div className="mt-8 flex flex-wrap gap-3">
              {data?.primary_cta?.label && (
                <Link
                  to={data.primary_cta.href || "#"}
                  data-testid="hero-primary-cta"
                  className="bg-[#538135] text-white hover:bg-[#09582D] transition-colors rounded-md px-5 py-3 text-sm font-semibold inline-flex items-center gap-2"
                >
                  {data.primary_cta.label} <Icons.ArrowRight size={16} />
                </Link>
              )}
              {data?.secondary_cta?.label && (
                <Link
                  to={data.secondary_cta.href || "#"}
                  data-testid="hero-secondary-cta"
                  className="bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 rounded-md px-5 py-3 text-sm font-semibold transition-colors"
                >
                  {data.secondary_cta.label}
                </Link>
              )}
            </div>
            {data?.trust_line && <p className="mt-8 text-xs text-gray-500 uppercase tracking-wider">{data.trust_line}</p>}
          </div>
          {data?.image && (
            <div className="lg:col-span-5">
              <div className={`relative overflow-hidden border border-[#E5E7EB] shadow-[0_20px_60px_rgba(63,101,40,0.12)] ${isSoftwareHero ? "aspect-[4/3] rounded-xl bg-white p-2" : "aspect-[4/5] rounded-2xl"}`}>
                <img src={isSoftwareHero ? workbenchImage : data.image} alt="SQIT Workbench interface" className={`w-full h-full ${isSoftwareHero ? "object-contain" : "object-cover"}`} />
                {!isSoftwareHero && <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-md border border-white/60 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#EAF4E3] flex items-center justify-center">
                      <Icons.Activity size={16} className="text-[#538135]" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-gray-900">Live job</div>
                      <div className="text-[11px] text-gray-500">SQIT-A3F91B · Running · 4 samples</div>
                    </div>
                  </div>
                </div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FeatureListBlock({ data }) {
  return (
    <Section>
      <SectionHead overline={data?.overline} heading={data?.heading} />
      <div className={`mt-12 grid grid-cols-1 md:grid-cols-2 ${data?.layout === "compact" ? "lg:grid-cols-4 gap-3" : "lg:grid-cols-4 gap-6"}`}>
        {(data?.features || []).map((f, i) => (
          <div key={i} className={`sqit-card sqit-card-hover ${data?.layout === "compact" ? "p-4" : "p-6"}`}>
            <div className="w-10 h-10 rounded-lg bg-[#EAF4E3] flex items-center justify-center text-[#538135]">
              <Icon name={f.icon} size={20} strokeWidth={1.75} />
            </div>
            <h3 className="mt-4 font-display font-semibold text-gray-900">{f.title}</h3>
            <p className="mt-2 text-sm text-gray-600 leading-relaxed">{f.text}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function CardsBlock({ data }) {
  return (
    <Section className="bg-[#F8FAF7]">
      <SectionHead overline={data?.overline} heading={data?.heading} />
      <div className={`mt-12 grid grid-cols-1 md:grid-cols-2 ${data?.layout === "compact" ? "lg:grid-cols-3 xl:grid-cols-6 gap-3" : "lg:grid-cols-3 gap-6"}`}>
        {(data?.cards || []).map((c, i) => (
          <div key={i} className={`sqit-card sqit-card-hover ${data?.layout === "compact" ? "p-4" : "p-6"}`}>
            <div className={`rounded-xl bg-[#538135] text-white flex items-center justify-center ${data?.layout === "compact" ? "w-9 h-9" : "w-11 h-11"}`}>
              <Icon name={c.icon} size={20} strokeWidth={1.75} />
            </div>
            <h3 className={`mt-4 font-display font-semibold text-gray-900 ${data?.layout === "compact" ? "text-sm" : "text-lg"}`}>{c.title}</h3>
            <p className={`${data?.layout === "compact" ? "mt-1 text-xs" : "mt-2 text-sm"} text-gray-600 leading-relaxed`}>{c.text}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function WorkflowBlock({ data }) {
  const steps = data?.steps || [];
  return (
    <Section>
      <SectionHead overline={data?.overline} heading={data?.heading} />
      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6">
        {steps.map((s, i) => (
          <div key={i} className="relative">
            <div className="text-6xl font-display font-bold text-[#EAF4E3]">{s.n}</div>
            <h3 className="mt-2 font-display font-semibold text-gray-900">{s.title}</h3>
            <p className="mt-2 text-sm text-gray-600 leading-relaxed">{s.text}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function TestimonialsBlock({ data }) {
  return (
    <Section className="bg-[#F8FAF7]">
      <SectionHead overline={data?.overline} heading={data?.heading} />
      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
        {(data?.quotes || []).map((q, i) => (
          <div key={i} className="sqit-card p-6">
            <Icons.Quote size={22} className="text-[#538135]/50" />
            <p className="mt-4 text-gray-700 leading-relaxed">"{q.quote}"</p>
            <div className="mt-6 border-t pt-4">
              <div className="font-semibold text-gray-900 text-sm">{q.author}</div>
              <div className="text-xs text-gray-500">{q.role}</div>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function CtaBlock({ data }) {
  return (
    <Section>
      <div className="relative overflow-hidden rounded-2xl bg-[#09582D] p-10 sm:p-16">
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, #fff 1px, transparent 1px), radial-gradient(circle at 80% 60%, #fff 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }}
        />
        <div className="relative flex flex-col lg:flex-row lg:items-center gap-6 justify-between">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-white">{data?.heading}</h2>
            {data?.subheading && <p className="mt-3 text-white/80 max-w-xl">{data.subheading}</p>}
          </div>
          <div className="flex gap-3">
            {data?.primary_cta?.label && (
              <Link to={data.primary_cta.href || "#"} data-testid="cta-primary"
                className="bg-white text-[#09582D] hover:bg-gray-100 rounded-md px-5 py-3 text-sm font-semibold transition-colors">
                {data.primary_cta.label}
              </Link>
            )}
            {data?.secondary_cta?.label && (
              <Link to={data.secondary_cta.href || "#"} data-testid="cta-secondary"
                className="bg-transparent border border-white/50 text-white hover:bg-white/10 rounded-md px-5 py-3 text-sm font-semibold transition-colors">
                {data.secondary_cta.label}
              </Link>
            )}
          </div>
        </div>
      </div>
    </Section>
  );
}

function RichTextBlock({ data }) {
  return (
    <Section>
      <div className="max-w-3xl">
        {data?.heading && (
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-gray-900 mb-6">{data.heading}</h2>
        )}
        <div className="sqit-prose" dangerouslySetInnerHTML={{ __html: data?.html || "" }} />
      </div>
    </Section>
  );
}

function TextBlock({ data }) {
  return (
    <Section>
      <div className="max-w-3xl">
        {data?.heading && (
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-gray-900 mb-4">{data.heading}</h2>
        )}
        {data?.body && (
          <p className="text-gray-700 leading-relaxed text-lg whitespace-pre-wrap">{data.body}</p>
        )}
      </div>
    </Section>
  );
}

function ImageBlock({ data }) {
  if (!data?.src) return null;
  return (
    <Section>
      <figure className="max-w-4xl mx-auto">
        <div className="rounded-2xl overflow-hidden border border-[#E5E7EB]">
          <img src={data.src} alt={data.alt || ""} className="w-full h-auto object-cover" />
        </div>
        {data.caption && (
          <figcaption className="mt-3 text-sm text-gray-500 text-center">{data.caption}</figcaption>
        )}
      </figure>
    </Section>
  );
}

function GalleryBlock({ data }) {
  const items = data?.items || [];
  if (items.length === 0) return null;
  return (
    <Section>
      <SectionHead heading={data?.heading} />
      <div className={`${data?.heading ? "mt-12" : ""} grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4`}>
        {items.map((it, i) => (
          <figure key={i} className="rounded-xl overflow-hidden border border-[#E5E7EB]">
            <img src={it.src} alt={it.alt || ""} className="w-full aspect-square object-cover" />
            {it.caption && <figcaption className="text-xs text-gray-500 p-2">{it.caption}</figcaption>}
          </figure>
        ))}
      </div>
    </Section>
  );
}

function StatsBlock({ data }) {
  const items = data?.items || [];
  return (
    <Section>
      <SectionHead overline={data?.overline} heading={data?.heading} />
      <div className={`${data?.heading ? "mt-12" : ""} grid grid-cols-2 md:grid-cols-4 ${items.length > 4 ? "xl:grid-cols-6" : ""} gap-4`}>
        {items.map((it, i) => (
          <div key={i} className="sqit-card p-6 text-center">
            <div className="font-display text-3xl sm:text-4xl font-bold text-[#09582D]">{it.value}</div>
            <div className="mt-2 text-sm text-gray-600">{it.label}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function FaqBlock({ data }) {
  const [open, setOpen] = useState(0);
  const items = data?.items || [];
  return (
    <Section>
      <SectionHead overline={data?.overline} heading={data?.heading} />
      <div className={`${data?.heading ? "mt-12" : ""} max-w-3xl space-y-3`}>
        {items.map((it, i) => (
          <div key={i} className="sqit-card overflow-hidden">
            <button
              type="button"
              onClick={() => setOpen(open === i ? -1 : i)}
              data-testid={`faq-toggle-${i}`}
              className="w-full flex items-center justify-between text-left px-5 py-4 hover:bg-[#F8FAF7]/60 transition-colors"
            >
              <span className="font-display font-medium text-gray-900">{it.question}</span>
              <Icons.ChevronDown
                size={16}
                className={`text-[#538135] transition-transform ${open === i ? "rotate-180" : ""}`}
              />
            </button>
            {open === i && (
              <div
                className="px-5 pb-5 sqit-prose text-sm text-gray-700"
                dangerouslySetInnerHTML={{ __html: it.answer || "" }}
              />
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

function ButtonGroupBlock({ data }) {
  const buttons = data?.buttons || [];
  const style = (s) => {
    if (s === "outline") return "border border-gray-300 text-gray-900 hover:bg-gray-50";
    if (s === "secondary") return "bg-[#EAF4E3] text-[#09582D] hover:bg-[#EAF4E3]/70";
    return "bg-[#538135] text-white hover:bg-[#09582D]";
  };
  return (
    <Section>
      {data?.heading && (
        <h2 className="font-display text-2xl sm:text-3xl font-semibold text-gray-900 mb-6">{data.heading}</h2>
      )}
      <div className="flex flex-wrap gap-3">
        {buttons.map((b, i) => (
          <Link
            key={i} to={b.href || "#"}
            className={`rounded-md px-5 py-3 text-sm font-semibold transition-colors ${style(b.style)}`}
          >
            {b.label}
          </Link>
        ))}
      </div>
    </Section>
  );
}

function LinkListBlock({ data }) {
  return (
    <Section>
      {data?.heading && (
        <h2 className="font-display text-xl font-semibold text-gray-900 mb-4">{data.heading}</h2>
      )}
      <ul className="space-y-2">
        {(data?.links || []).map((l, i) => (
          <li key={i}>
            <Link to={l.href || "#"} className="text-[#538135] hover:text-[#09582D] text-sm inline-flex items-center gap-1">
              {l.label} <Icons.ArrowUpRight size={12} />
            </Link>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function ListBlock({ data }) {
  return (
    <Section>
      <div className="max-w-3xl">
        {data?.heading && (
          <h2 className="font-display text-2xl font-semibold text-gray-900 mb-4">{data.heading}</h2>
        )}
        <ul className="list-disc pl-6 space-y-2 text-gray-700">
          {(data?.items || []).map((it, i) => <li key={i}>{it}</li>)}
        </ul>
      </div>
    </Section>
  );
}

// ---------- Renderer ----------

export const BLOCK_RENDERERS = {
  hero: HeroBlock,
  "feature-list": FeatureListBlock,
  cards: CardsBlock,
  workflow: WorkflowBlock,
  testimonials: TestimonialsBlock,
  cta: CtaBlock,
  richtext: RichTextBlock,
  text: TextBlock,
  image: ImageBlock,
  gallery: GalleryBlock,
  stats: StatsBlock,
  faq: FaqBlock,
  "button-group": ButtonGroupBlock,
  "link-list": LinkListBlock,
  list: ListBlock,
};

export default function PageRenderer({ page }) {
  if (!page) return null;
  return (
    <div data-testid={`cms-page-${page.slug}`}>
      {(page.sections || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((sec) => (
        <div key={sec.key} data-section={sec.key}>
          {(sec.blocks || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((b) => {
            const Comp = BLOCK_RENDERERS[b.type];
            return Comp ? <Comp key={b.key} data={b.data} /> : null;
          })}
        </div>
      ))}
    </div>
  );
}
